import { lazy, Suspense, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import SeoJsonLd from "@/components/SeoJsonLd";
import Header from "@/components/landing/Header";
import Hero from "@/components/landing/Hero";
import Footer from "@/components/landing/Footer";
import StickyMobileCTA from "@/components/landing/StickyMobileCTA";
import { capturePosthog } from "@/lib/posthog";

const PainPoints = lazy(() => import("@/components/landing/PainPoints"));
const HowItWorks = lazy(() => import("@/components/landing/HowItWorks"));
const SocialProof = lazy(() => import("@/components/landing/SocialProof"));
const Features = lazy(() => import("@/components/landing/Features"));
const PricingFAQ = lazy(() => import("@/components/landing/PricingFAQ"));

const AsymmetricDivider = () => (
  <div className="max-w-6xl mx-auto px-4">
    <div className="divider-asymmetric" />
  </div>
);

/**
 * Rendu dans le même Suspense que les sections chargées à la demande : son effet ne part
 * qu'une fois ces sections affichées. Sans lui, un lien « Tarifs » ou « FAQ » venu d'une autre
 * page (/#pricing) laissait le visiteur en haut de l'accueil, la cible n'existant pas encore.
 */
const ScrollToHashOnReady = () => {
  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (!id) return;
    document.getElementById(id)?.scrollIntoView({ behavior: "instant", block: "start" });
  }, []);
  return null;
};

const Index = () => {
  return (
    <div className="relative min-h-screen bg-background noise-overlay">
      <Helmet>
        <html lang="fr-FR" />
        <title>Logiciel de planning supérette & franchise alimentaire IDCC 1505 | Equiply</title>
        <meta
          name="description"
          content="Planning, paie et registre du personnel pour gérants de supérette, épicerie et franchise alimentaire de proximité. 100 % conforme convention IDCC 1505. Essai 14 jours, sans CB."
        />
        <meta name="keywords" content="logiciel planning supérette, planning IDCC 1505, logiciel RH franchise alimentaire, planning Carrefour Express, planning Carrefour City, planning Carrefour Contact, planning magasin de proximité, registre du personnel commerce, export paie supérette" />
        <link rel="canonical" href="https://www.equiply.fr/" />
        <meta property="og:url" content="https://www.equiply.fr/" />
        <meta property="og:title" content="Logiciel de planning supérette & franchise alimentaire IDCC 1505 | Equiply" />
        <meta
          property="og:description"
          content="Planning, paie et registre du personnel pour gérants de supérette, épicerie et franchise alimentaire de proximité. 100 % conforme convention IDCC 1505. Essai 14 jours, sans CB."
        />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Logiciel de planning supérette & franchise alimentaire IDCC 1505 | Equiply" />
        <meta name="twitter:description" content="Planning, paie et registre du personnel pour gérants de supérette, épicerie et franchise alimentaire de proximité. Conforme IDCC 1505." />
      </Helmet>
      <SeoJsonLd />
      <Header />
      <main>
        <Hero />
        <Suspense fallback={<div className="min-h-[200px]" aria-hidden="true" />}>
          <PainPoints />
          <div className="h-8" />
          <HowItWorks />
          <SocialProof />
          <AsymmetricDivider />
          <Features />
          <div className="h-12" />
          <PricingFAQ />
          <ScrollToHashOnReady />
        </Suspense>
        {/* Final CTA after FAQ — rendu d'emblée : différé, il restait vide quand on arrivait vite en bas de page */}
        <section className="py-24 relative overflow-hidden bg-foreground">
          {/* Geometric cross pattern instead of blobs */}
          <div className="absolute inset-0 w-full h-full bg-cross-pattern opacity-60" />

          <div className="container mx-auto px-4 text-center relative z-10">
            <h2 className="text-3xl md:text-5xl font-bold tracking-[-0.03em] !text-background mb-6">
              Prêt à <span className="text-display-headline font-normal !text-background">simplifier</span> vos plannings ?
            </h2>
            <p className="text-lg md:text-xl !text-background/80 mb-10 max-w-2xl mx-auto">
              Rejoignez les gérants qui ont arrêté de perdre du temps sur Excel, et commencez à créer des plannings sans erreur en un temps record.
            </p>
            
            <Button size="lg" asChild className="text-base px-8 h-14 bg-primary hover:bg-primary/90 !text-primary-foreground shadow-cinematic transition-[transform,background-color] duration-base hover:scale-[1.02] group [&_*]:!text-primary-foreground">
              <Link
                to="/auth?mode=signup"
                onClick={() => {
                  capturePosthog("landing_cta_try_free_clicked", {
                    cta_id: "final_section_try_free",
                    cta_label: "Creer mon premier planning",
                    cta_section: "final_cta",
                  });
                  capturePosthog("landing_cta_clicked", {
                    cta_id: "final_section_try_free",
                    cta_label: "Creer mon premier planning",
                    cta_section: "final_cta",
                  });
                }}
              >
                Créer mon premier planning
                <ArrowRight className="w-5 h-5 ml-2 transition-transform duration-base group-hover:translate-x-1" />
              </Link>
            </Button>
            
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4 text-payfit-body !text-background/70">
               <span>Essai gratuit 14 jours</span>
               <span className="w-1.5 h-1.5 rounded-full bg-background/40" />
               <span>Sans carte bancaire</span>
               <span className="w-1.5 h-1.5 rounded-full bg-background/40" />
               <span>Sans engagement</span>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      {/* Réserve la hauteur du bouton collant mobile : sans elle, il masquait la dernière ligne du pied de page. */}
      <div aria-hidden className="h-[4.5rem] bg-foreground md:hidden" />
      <StickyMobileCTA />
    </div>
  );
};

export default Index;
